import { useQuery } from '@tanstack/react-query';
import { loadShopifyCollectionByHandle } from '@portal/api/shopifyV2/collection';
import { StoreCode } from '@portal/schema/storeCode';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { useIsLoggedInValue } from '@portal/appLayer/auth';
import {
  consolidateFlavorProducts,
  removeDuplicateProducts
} from '@portal/shared/utils/shop/product/flavors';
import { Identifier } from '@portal/types/shopify/request.types';

interface GetBundlesCollectionQueryProps {
  storeCode: StoreCode;
  collectionHandle: string;
  isEnabled?: boolean;
  identifiers?: Identifier[];
  showMemberPrices?: boolean;
  isLoggedIn: boolean;
}

function useShopListProductsCollectionQuery({
  storeCode,
  collectionHandle,
  identifiers
}: GetBundlesCollectionQueryProps) {
  return useQuery({
    queryKey: ['shop-list-products', storeCode, collectionHandle, identifiers],
    queryFn: () => loadShopifyCollectionByHandle(storeCode, collectionHandle, identifiers),
    enabled: true
  });
}

export function useShopListProducts(
  collectionHandle: string,
  identifiers?: Identifier[],
  options: { hideDuplicateProducts: boolean; showMemberPrices: boolean } = {
    hideDuplicateProducts: false,
    showMemberPrices: false
  }
) {
  const isLoggedIn = useIsLoggedInValue();
  const store = useStoreValue();

  const { data: collection, isPending } = useShopListProductsCollectionQuery({
    storeCode: store,
    collectionHandle,
    showMemberPrices: options.showMemberPrices || false,
    isLoggedIn,
    ...(identifiers && { identifiers })
  });

  const flavorsMap = consolidateFlavorProducts(collection?.products, isLoggedIn);

  if (options.hideDuplicateProducts) {
    return {
      isPending,
      data: {
        products: removeDuplicateProducts(collection?.products, {
          keepFlavorProducts: true,
          showMemberPrices: options.showMemberPrices || false
        }),
        flavorsMap
      }
    };
  }

  return {
    isPending,
    data: {
      products: collection?.products || [],
      flavorsMap
    }
  };
}
